import { useEffect, useState } from "react"
import { router, useForm, usePage } from "@inertiajs/react"
import { Chess } from "chess.js"
import clsx from "clsx"

import { CheckCircleIcon } from "@heroicons/react/20/solid"
import usePuzzleAnalytics from "@/hooks/usePuzzleAnalytics"
import { useGameMoves } from "@/hooks/useGameMoves"
import { usePuzzle } from "@/hooks/usePuzzle"

import ScrollProvider from "../ScrollProvider"
import { ActiveTab } from "./PuzzleScreen"
import MovesList from "../game/MovesList"
import { Dice } from "../icons"
import Button from "../Button"
import Tab from "./Tab"
import ErrorMessage from "../ErrorMessage"
import { PuzzleShowProps } from "@/types/types"

const Sidebar = ({
  isOwner,
  activeTab,
  setActiveTab,
}: {
  isOwner: boolean
  activeTab: ActiveTab
  setActiveTab: (tab: ActiveTab) => void
}) => {
  const {
    id,
    title,
    description,
    discoverable,
    draft,
    chess960,
    fen,
    start,
    moves,
    toMove,
    setToMove,
  } = usePuzzle()
  const { related_puzzles, random_puzzle_id } = usePage<PuzzleShowProps>().props
  const { movesHistory, undoMove, currentMove, activeMove, hasNextMove } =
    useGameMoves()
  const [editDetails, setEditDetails] = useState(!Boolean(title))
  const [showSolution, setShowSolution] = useState(false)
  const [boardError, setBoardError] = useState<string | null>(null)
  const { logPuzzleCompleted, percentageFirstTry } = usePuzzleAnalytics()

  const {
    data,
    setData,
    patch,
    errors,
    setError,
    processing: isUpdatingPuzzle,
  } = useForm({
    title: title ?? "",
    description: description ?? "",
    discoverable: discoverable ?? false,
  })

  useEffect(() => {
    if (!hasNextMove && !showSolution) {
      // Solved puzzle
      setShowSolution(true)
      logPuzzleCompleted()
    }
  }, [hasNextMove, showSolution, logPuzzleCompleted])

  const saveStartPosition = async () => {
    setBoardError(null)

    try {
      const chess = new Chess(undefined, { chess960 })
      chess.load(fen)
    } catch (error) {
      if (error instanceof Error) {
        setBoardError(error.message)
      }

      return
    }

    router.patch(
      `/puzzles/${id}`,
      { start: fen },
      { async: true, onSuccess: () => setActiveTab(1) }
    )
  }

  const saveSolution = async () => {
    router.patch(
      `/puzzles/${id}`,
      { moves: movesHistory as any },
      { async: true, onSuccess: () => setActiveTab(2) }
    )
  }

  const saveDetails = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!data.title) setError("title", "Title is required.")
    if (!data.description) setError("description", "Description is required.")
    if (!data.title || !data.description) return

    patch(`/puzzles/${id}`, {
      async: true,
      onSuccess: () => setEditDetails(false),
    })
  }

  return (
    <ScrollProvider dependency={activeMove}>
      {(movesContainerRef) => (
        <div
          className={clsx(
            "flex grow flex-col-reverse overflow-y-auto border-t border-gray-200 bg-white min-[1500px]:w-[500px] lg:fixed lg:top-18 lg:right-0 lg:bottom-0 lg:z-50 lg:flex lg:w-[335px] lg:flex-col lg:border-t-0 lg:border-l xl:w-96"
          )}
        >
          <div className="pb-safe flex-1 border-t border-gray-200 bg-gray-50 lg:border-t-0 lg:border-b lg:pb-0">
            {isOwner ? (
              <div className="divide-y divide-gray-200 border-b border-gray-200">
                <Tab
                  index={0}
                  activeTab={activeTab}
                  title="Starting position"
                  completed={!!start}
                >
                  {(active) => (
                    <div className="mt-3 space-y-4">
                      {active ? (
                        <>
                          <div className="text-sm text-gray-500">
                            <div>
                              Set the starting position of the puzzle by
                              dragging and dropping the pieces. To remove a
                              piece, drag it off the board.
                            </div>
                            <div className="mt-2">
                              The starting position can not be changed after
                              saving.
                            </div>
                          </div>

                          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-100 p-3 font-mono text-sm text-gray-600">
                            {fen}
                          </div>

                          <div className="mt-4 flex items-center gap-x-4">
                            <div className="text-sm leading-6 font-medium text-gray-900">
                              To move
                            </div>
                            <div className="isolate inline-flex rounded-md shadow-xs">
                              <button
                                type="button"
                                onClick={() => setToMove("w")}
                                className={clsx(
                                  "focus-visible:outline-chessdrop-200 relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 focus-visible:outline-4 focus-visible:outline-offset-0",
                                  toMove === "w"
                                    ? "bg-chessdrop-500 ring-chessdrop-700 hover:bg-chessdrop-600 text-white"
                                    : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
                                )}
                              >
                                White
                              </button>
                              <button
                                type="button"
                                onClick={() => setToMove("b")}
                                className={clsx(
                                  "focus-visible:outline-chessdrop-200 relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 focus-visible:outline-4 focus-visible:outline-offset-0",
                                  toMove === "b"
                                    ? "bg-chessdrop-500 ring-chessdrop-700 hover:bg-chessdrop-600 text-white"
                                    : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
                                )}
                              >
                                Black
                              </button>
                            </div>
                          </div>

                          <Button
                            onClick={saveStartPosition}
                            size="sm"
                            style="primary"
                            loading={isUpdatingPuzzle}
                            className="!mt-8"
                          >
                            Save starting position
                          </Button>

                          {boardError && (
                            <p className="text-sm font-medium text-red-600">
                              {boardError}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-100 p-3 font-mono text-sm text-gray-600">
                          {fen}
                        </div>
                      )}
                    </div>
                  )}
                </Tab>

                <Tab
                  index={1}
                  activeTab={activeTab}
                  title="Solution"
                  completed={!!movesHistory?.length}
                >
                  {(active) => (
                    <div>
                      {active ? (
                        <>
                          <div className="mt-3 text-sm text-gray-500">
                            <div>Play out your solution to the puzzle.</div>
                            <div className="mt-2">
                              The solution can not be changed after saving.
                            </div>
                          </div>

                          <div className="-mx-6">
                            <div
                              ref={movesContainerRef}
                              className="flex min-h-[100px] flex-shrink-0 flex-col items-start justify-start space-y-1.5 overflow-auto scroll-smooth px-6 pt-6 pb-3"
                            >
                              <MovesList depth={0} moves={movesHistory} />
                            </div>
                          </div>
                          {movesHistory?.length > 0 && (
                            <div className="flex gap-x-2 pt-3">
                              <Button
                                onClick={undoMove}
                                size="sm"
                                style="secondary"
                                disabled={!currentMove || isUpdatingPuzzle}
                              >
                                Undo move
                              </Button>
                              <Button
                                onClick={saveSolution}
                                size="sm"
                                style="primary"
                                loading={isUpdatingPuzzle}
                              >
                                Save solution
                              </Button>
                            </div>
                          )}
                        </>
                      ) : movesHistory?.length > 0 ? (
                        <div className="-mx-6">
                          <div
                            ref={movesContainerRef}
                            className="flex min-h-20 flex-shrink-0 flex-col items-start justify-start space-y-1.5 overflow-auto scroll-smooth px-6 pt-6"
                          >
                            <MovesList depth={0} moves={movesHistory} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </Tab>

                <Tab
                  index={2}
                  activeTab={activeTab}
                  title="Details"
                  completed={Boolean(title) && Boolean(description)}
                >
                  {(active) =>
                    active ? (
                      <>
                        {editDetails ? (
                          <form
                            onSubmit={saveDetails}
                            className="mt-3 space-y-4"
                          >
                            <div>
                              <label
                                htmlFor="title"
                                className="block text-sm leading-6 font-medium text-gray-900"
                              >
                                Title
                              </label>
                              <input
                                type="text"
                                id="title"
                                className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 border-gray-200 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                                required
                                value={data.title}
                                onChange={(e) =>
                                  setData("title", e.target.value)
                                }
                              />
                              <ErrorMessage
                                className="mt-1.5"
                                message={errors.title}
                              />
                            </div>

                            <div>
                              <label
                                htmlFor="description"
                                className="block text-sm leading-6 font-medium text-gray-900"
                              >
                                Description
                              </label>
                              <textarea
                                rows={3}
                                id="description"
                                placeholder="White to move and win"
                                className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 border-gray-200 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                                required
                                value={data.description}
                                onChange={(e) =>
                                  setData("description", e.target.value)
                                }
                              />
                              <ErrorMessage
                                className="mt-1.5"
                                message={errors.description}
                              />
                            </div>

                            <div>
                              <div className="text-sm leading-6 font-medium text-gray-900">
                                Discoverable?
                              </div>

                              <div className="isolate mt-1 inline-flex rounded-md shadow-xs">
                                <button
                                  type="button"
                                  onClick={() => setData("discoverable", true)}
                                  className={clsx(
                                    "focus-visible:outline-chessdrop-200 relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 focus-visible:outline-4 focus-visible:outline-offset-0",
                                    data.discoverable
                                      ? "bg-chessdrop-500 ring-chessdrop-700 hover:bg-chessdrop-600 text-white"
                                      : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
                                  )}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setData("discoverable", false)}
                                  className={clsx(
                                    "focus-visible:outline-chessdrop-200 relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 focus-visible:outline-4 focus-visible:outline-offset-0",
                                    !data.discoverable
                                      ? "bg-chessdrop-500 ring-chessdrop-700 hover:bg-chessdrop-600 text-white"
                                      : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
                                  )}
                                >
                                  No
                                </button>
                              </div>

                              <div className="mt-2 text-sm text-gray-500">
                                Discoverable puzzles can be found randomly by
                                other ChessDrop users when working through
                                puzzles. The remainder of your data, games and
                                events, remain private.
                              </div>
                            </div>

                            <div className="!mt-8 flex gap-x-2">
                              {!draft && (
                                <Button
                                  style="secondary"
                                  type="button"
                                  onClick={() => setEditDetails(false)}
                                >
                                  Cancel
                                </Button>
                              )}
                              <Button
                                loading={isUpdatingPuzzle}
                                type="submit"
                                fullWidth
                                className="flex-1"
                              >
                                Save details
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <dl className="mt-3 space-y-4">
                              <div>
                                <dt className="text-sm font-medium tracking-tight text-gray-900">
                                  Title
                                </dt>
                                <dd className="mt-1 text-sm text-gray-500">
                                  {title}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium tracking-tight text-gray-900">
                                  Description
                                </dt>
                                <dd className="mt-1 text-sm whitespace-pre-wrap text-gray-500">
                                  {description ?? "-"}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-sm font-medium tracking-tight text-gray-900">
                                  Discoverable
                                </dt>
                                <dd className="mt-1 text-sm whitespace-pre-wrap text-gray-500">
                                  {discoverable ? "Yes" : "No"}
                                </dd>
                              </div>
                            </dl>
                            <Button
                              onClick={() => setEditDetails(true)}
                              style="secondary"
                              className="mt-4"
                            >
                              Edit details
                            </Button>
                          </>
                        )}
                      </>
                    ) : null
                  }
                </Tab>
              </div>
            ) : (
              <div className="flex h-full flex-col">
                <div className="divide-y divide-gray-200 border-b border-gray-200">
                  <div className="p-6">
                    <div className="text-base font-semibold tracking-tight text-gray-900">
                      Move the pieces to try to solve this user-contributed
                      puzzle.
                    </div>
                    <div className="mt-1 min-h-5 text-sm font-medium text-gray-500">
                      {percentageFirstTry}% of people got this right on the
                      first attempt.
                    </div>
                  </div>

                  <div className="p-6">
                    <h2 className="text-base font-semibold tracking-tight text-gray-900">
                      {title}
                    </h2>
                    <p className="mt-1 text-sm font-medium whitespace-pre-wrap text-gray-500">
                      {description ?? "-"}
                    </p>
                  </div>

                  {moves && (
                    <div className="p-6">
                      <h2 className="sr-only">Solution</h2>
                      <div className="relative -mx-6">
                        <div
                          ref={movesContainerRef}
                          className={clsx(
                            "flex min-h-20 flex-shrink-0 flex-col items-start justify-start space-y-1.5 scroll-smooth px-6 pt-3 pb-3",
                            showSolution ? "overflow-auto" : "overflow-hidden"
                          )}
                        >
                          <MovesList
                            depth={0}
                            moves={moves}
                            hideFuture={!showSolution}
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex min-h-9 flex-wrap items-center gap-x-1.5 gap-y-2">
                        {!showSolution && (
                          <Button
                            onClick={() => setShowSolution(true)}
                            size="sm"
                            style="secondary"
                          >
                            Show solution
                          </Button>
                        )}

                        {showSolution && random_puzzle_id && (
                          <Button
                            size="sm"
                            href={`/puzzles/${random_puzzle_id}`}
                            asAnchorTag
                          >
                            <Dice className="size-3" />
                            <span className="ml-1">Random puzzle</span>
                          </Button>
                        )}

                        {!hasNextMove && (
                          <div className="inline-flex flex-shrink-0 items-center justify-center gap-x-2 rounded-xl bg-green-50 py-2 pr-4 pl-3 ring-1 ring-green-600/20 ring-inset">
                            <CheckCircleIcon className="size-5 flex-shrink-0 text-green-400" />
                            <span className="text-sm font-semibold text-green-700">
                              Puzzle solved!
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {related_puzzles.length > 0 && (
                  <div className="flex flex-1 flex-col justify-end">
                    <div className="border-t border-gray-200 py-6 transition duration-300 data-[closed]:opacity-0">
                      <h3 className="px-6 text-base font-semibold tracking-tight text-gray-900">
                        More from the creator
                      </h3>
                      <div className="mt-3 flex flex-col">
                        {related_puzzles?.map((puzzle) => (
                          <a
                            key={puzzle.id}
                            href={`/puzzles/${puzzle.id}`}
                            className="flex h-10 items-center border border-transparent bg-transparent px-6 text-sm text-gray-900 hover:bg-gray-200 focus-visible:bg-gray-200 focus-visible:outline-none"
                          >
                            <span className="line-clamp-1">{puzzle.title}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </ScrollProvider>
  )
}

export default Sidebar
